import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Template } from '../../../mocks/websiteTemplates';
import { nameToSlug } from '../websiteBuilderConstants';

interface Props {
  templates: Template[];
  onPreview: (t: Template) => void;
}

function pickWeeklyTemplate(templates: Template[]): Template {
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const weekNum = Math.floor(Date.now() / weekMs);
  const pool = templates.filter((t) => t.badge === 'Popular');
  return pool[weekNum % pool.length];
}

function getDaysLeft(): number {
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const weekNum = Math.floor(now / weekMs);
  const weekEnd = (weekNum + 1) * weekMs;
  return Math.max(1, Math.ceil((weekEnd - now) / (24 * 60 * 60 * 1000)));
}

function getWeekLabel(): string {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `Week ${weekNum}, ${now.getFullYear()}`;
}

const DISMISS_KEY = 'totw-dismissed-week';

const TAG_DOT_COLORS: Record<string, string> = {
  Dark: 'bg-slate-400',
  Minimal: 'bg-gray-300',
  Elegant: 'bg-rose-300',
  Bold: 'bg-orange-400',
  Modern: 'bg-violet-400',
  Colorful: 'bg-pink-400',
  Warm: 'bg-amber-400',
  Clean: 'bg-sky-400',
  Corporate: 'bg-slate-400',
  Creative: 'bg-fuchsia-400',
};

export default function TemplateOfTheWeek({ templates, onPreview }: Props) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [daysLeft] = useState(getDaysLeft);
  const [expanded, setExpanded] = useState(false);

  // Dismiss per-week via sessionStorage
  useEffect(() => {
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const weekNum = Math.floor(Date.now() / weekMs);
    const stored = sessionStorage.getItem(DISMISS_KEY);
    if (stored === String(weekNum)) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const weekNum = Math.floor(Date.now() / weekMs);
    sessionStorage.setItem(DISMISS_KEY, String(weekNum));
    setDismissed(true);
  };

  const template = pickWeeklyTemplate(templates);
  if (dismissed || !template) return null;

  return (
    <div className="sticky top-24 z-30 mb-10">
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f0f14 0%, #1a1128 40%, #111827 100%)',
        }}
      >
        {/* Ambient glow blobs */}
        <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full opacity-20 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)', filter: 'blur(40px)' }} />

        {/* Main content row */}
        <div className="relative z-10 flex items-center gap-8 px-8 py-6">

          {/* LEFT: Info */}
          <div className="flex-1 min-w-0">
            {/* Top labels row */}
            <div className="flex items-center gap-3 mb-3">
              {/* Badge */}
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10">
                <div className="relative w-2 h-2 flex items-center justify-center">
                  <span className="absolute w-2 h-2 rounded-full bg-amber-400 animate-ping opacity-75"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                </div>
                <span className="text-xs font-bold text-amber-400 uppercase tracking-widest whitespace-nowrap">Template of the Week</span>
              </div>
              {/* Week label */}
              <span className="text-xs text-white/30 font-medium hidden sm:block">{getWeekLabel()}</span>
              {/* Category */}
              <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-white/10 text-white/70 border border-white/10 whitespace-nowrap">
                {template.category}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-black text-white leading-tight mb-2 truncate">
              {template.name}
            </h2>

            {/* Description */}
            <p className="text-sm text-white/55 leading-relaxed mb-4 line-clamp-2 max-w-lg">
              {template.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-5">
              {template.tags.map((tag) => (
                <div key={tag} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 border border-white/10">
                  <div className={`w-1.5 h-1.5 rounded-full ${TAG_DOT_COLORS[tag] ?? 'bg-gray-400'}`}></div>
                  <span className="text-xs text-white/60 font-medium">{tag}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/website-builder/template/${nameToSlug(template.name)}`)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-gray-900 cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}
              >
                <i className="ri-eye-line"></i>
                View Details
              </button>
              <button
                onClick={() => onPreview(template)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white border border-white/20 bg-white/10 hover:bg-white/15 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-layout-line"></i>
                Quick Preview
              </button>
            </div>
          </div>

          {/* CENTER: Browser mockup screenshot */}
          <div className="flex-shrink-0 relative" style={{ width: 320 }}>
            {/* Decorative tilt card */}
            <div
              className="absolute inset-0 rounded-xl border border-white/5 bg-white/5"
              style={{ transform: 'rotate(2deg) scale(0.98)', zIndex: 0 }}
            ></div>
            <div
              className="relative rounded-xl overflow-hidden border border-white/15"
              style={{ zIndex: 1 }}
            >
              {/* Browser chrome */}
              <div className="bg-[#1e1e2e] px-3 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/70"></div>
                </div>
                <div className="flex-1 bg-white/5 rounded px-2 py-0.5 flex items-center gap-1">
                  <i className="ri-lock-line text-white/30 text-xs"></i>
                  <span className="text-xs text-white/30 font-mono truncate">openclaw.ai/preview</span>
                </div>
              </div>
              {/* Screenshot */}
              <div style={{ height: 160 }} className="overflow-hidden">
                <img
                  src={template.image}
                  alt={template.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* RIGHT: Countdown + stats */}
          <div className="flex-shrink-0 flex flex-col items-center gap-4">
            {/* Countdown ring */}
            <div className="relative flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 border-dashed border-amber-400/30 bg-amber-400/5">
              <span className="text-2xl font-black text-amber-400 leading-none">{daysLeft}</span>
              <span className="text-xs text-amber-400/60 font-medium leading-none mt-0.5">{daysLeft === 1 ? 'day' : 'days'}</span>
              <span className="text-[9px] text-white/30 mt-0.5 uppercase tracking-wide">left</span>
            </div>
            <p className="text-xs text-white/30 text-center leading-tight max-w-16">Resets every Monday</p>

            {/* Expand / collapse toggle */}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/8 border border-white/10 text-white/40 hover:text-white/80 hover:bg-white/15 transition-all cursor-pointer"
              title={expanded ? 'Collapse' : 'More info'}
            >
              <i className={`text-sm transition-transform ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Expanded panel */}
        {expanded && (
          <div className="relative z-10 border-t border-white/8 px-8 py-5 grid grid-cols-4 gap-6">
            {[
              { icon: 'ri-layout-responsive-line', label: 'Responsive', value: 'All devices' },
              { icon: 'ri-palette-line', label: 'Customizable', value: 'Full control' },
              { icon: 'ri-speed-line', label: 'Performance', value: '99 / 100' },
              { icon: 'ri-seo-line', label: 'SEO Score', value: 'Optimized' },
            ].map(({ icon, label, value }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/8 border border-white/10 flex-shrink-0">
                  <i className={`${icon} text-white/60 text-base`}></i>
                </div>
                <div>
                  <p className="text-xs text-white/40">{label}</p>
                  <p className="text-sm font-bold text-white/80">{value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          title="Dismiss until next week"
          className="absolute top-4 right-4 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-white/8 border border-white/10 text-white/30 hover:text-white/70 hover:bg-white/15 transition-all cursor-pointer"
        >
          <i className="ri-close-line text-sm"></i>
        </button>

        {/* Bottom shimmer line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, #7c3aed80, #06b6d480, transparent)' }}
        />
      </div>
    </div>
  );
}
